"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useSession } from "next-auth/react";

export type Language = {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
};

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: "en", name: "English", nativeName: "English", flag: "🇺🇸" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
  { code: "de", name: "German", nativeName: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italian", nativeName: "Italiano", flag: "🇮🇹" },
  { code: "pt", name: "Portuguese", nativeName: "Português", flag: "🇵🇹" },
  { code: "zh", name: "Chinese", nativeName: "中文", flag: "🇨🇳" },
  { code: "ja", name: "Japanese", nativeName: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "Korean", nativeName: "한국어", flag: "🇰🇷" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇸🇦" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी", flag: "🇮🇳" },
  { code: "ru", name: "Russian", nativeName: "Русский", flag: "🇷🇺" },
];

type TranslationContextType = {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  translateText: (text: string, targetLanguage?: string) => Promise<string>;
  isTranslating: boolean;
  autoTranslate: boolean;
  setAutoTranslate: (enabled: boolean) => void;
  preferredLanguages: string[];
  setPreferredLanguages: (languages: string[]) => void;
};

const TranslationContext = createContext<TranslationContextType | undefined>(
  undefined,
);

export function TranslationProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const isAuthenticated = !!session?.user;

  const [currentLanguage, setCurrentLanguage] = useState<Language>(
    SUPPORTED_LANGUAGES[0],
  );
  const [isTranslating, setIsTranslating] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [preferredLanguages, setPreferredLanguages] = useState<string[]>([
    "en",
  ]);

  // Save language preferences to DB (for authenticated users)
  const saveToDb = useCallback(
    (patch: Record<string, unknown>) => {
      if (!isAuthenticated) return;
      fetch("/api/user/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessibilitySettings: patch }),
      }).catch(() => {});
    },
    [isAuthenticated]
  );

  // Load preferences on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetch("/api/user/preferences")
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          const a11y = data?.accessibilitySettings ?? {};
          if (a11y.language) {
            const lang = SUPPORTED_LANGUAGES.find((l) => l.code === a11y.language);
            if (lang) {
              setCurrentLanguage(lang);
              document.documentElement.lang = lang.code;
              document.documentElement.dir = lang.code === "ar" ? "rtl" : "ltr";
            }
          } else {
            // Detect browser language for new users
            const browserLang = navigator.language.split("-")[0];
            const detected = SUPPORTED_LANGUAGES.find((l) => l.code === browserLang);
            if (detected) setCurrentLanguage(detected);
          }
          // Auto-translate is not wired to a real service — never enable it.
          if (Array.isArray(a11y.preferredLanguages)) setPreferredLanguages(a11y.preferredLanguages);
        })
        .catch(() => {});
    } else {
      // Unauthenticated: use browser language
      const browserLang = navigator.language.split("-")[0];
      const detected = SUPPORTED_LANGUAGES.find((l) => l.code === browserLang);
      if (detected) setCurrentLanguage(detected);
    }
  }, [isAuthenticated]);

  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
    saveToDb({ language: language.code });
    document.documentElement.lang = language.code;
    document.documentElement.dir = language.code === "ar" ? "rtl" : "ltr";
  };

  const translateText = async (
    text: string,
    targetLanguage?: string,
  ): Promise<string> => {
    if (!text.trim()) return text;

    const target = targetLanguage || currentLanguage.code;
    if (target === "en") return text; // Assume source is English

    return text;
  };

  const updateAutoTranslate = (_enabled: boolean) => {
    setAutoTranslate(false);
    saveToDb({ autoTranslate: false });
  };

  const updatePreferredLanguages = (languages: string[]) => {
    setPreferredLanguages(languages);
    saveToDb({ preferredLanguages: languages });
  };

  return (
    <TranslationContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        translateText,
        isTranslating,
        autoTranslate,
        setAutoTranslate: updateAutoTranslate,
        preferredLanguages,
        setPreferredLanguages: updatePreferredLanguages,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
}
